import type { AgentMessage } from '../types';

/** User explicitly wants the canvas panel open (not bare "optimize resume"). */
export function detectExplicitCanvasOpenIntent(userText: string): boolean {
  const t = userText.toLowerCase();
  return (
    /\b(open|show|launch|start|toggle)\s+(canvas|optimizer|optimize panel|resume optimizer|resume canvas)\b/.test(t)
    || /\b(open|take me to)\b.{0,30}\b(optimi[sz]er|resume canvas|optimization canvas)\b/.test(t)
  );
}

/** User wants resume optimization — may still need JD collection first. */
export function detectOptimizeResumeIntent(userText: string): boolean {
  const t = userText.toLowerCase().trim();
  if (detectExplicitCanvasOpenIntent(t)) return false;
  return (
    /\b(optimi[sz]e (my )?resume|tailor (my )?resume|rewrite (my )?resume)\b/.test(t)
    || /\b(resume optimi[sz]er|single.?optimize|ats optimi[sz]e)\b/.test(t)
    || /\bwrite (my )?resume for\b/.test(t)
  );
}

/** Heuristic — pasted job descriptions are long or contain hiring keywords. */
export function looksLikeJobDescription(userText: string): boolean {
  const t = userText.trim();
  if (t.length < 120) return false;
  const lower = t.toLowerCase();
  const keywordHits = [
    /\b(responsibilit|requirement|qualification|about (the )?role|job description)\b/,
    /\b(we are looking|you will|must have|nice to have|years of experience)\b/,
    /\b(key responsibilit|what you.?ll do|what we're looking for)\b/,
    /\b(senior|junior|lead|manager|engineer|developer|product owner)\b.{0,40}\b(at|@)\b/,
  ].filter((re) => re.test(lower)).length;
  return t.length >= 280 || keywordHits >= 2 || (t.length >= 160 && keywordHits >= 1);
}

export function parseJobMetaFromText(text: string): { title: string; company: string } {
  const titleMatch =
    text.match(/(?:job title|role|position|opening)\s*[:\-–]\s*([^\n]+)/i)
    ?? text.match(/^([A-Z][^\n]{8,80})$/m);
  const companyMatch =
    text.match(/(?:company|employer|organization)\s*[:\-–]\s*([^\n]+)/i)
    ?? text.match(/\bat\s+([A-Z][A-Za-z0-9&.\s]{2,60})(?:\s*[\n|,.]|$)/);

  const firstLine = text.split('\n').map((l) => l.trim()).find((l) => l.length > 3) ?? '';

  return {
    title: titleMatch?.[1]?.trim().slice(0, 120) || firstLine.slice(0, 80) || 'Target Role',
    company: companyMatch?.[1]?.trim().slice(0, 80) || 'Target Company',
  };
}

export function lastAssistantAwaitingOptimizeJd(messages: AgentMessage[]): boolean {
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  if (!lastAssistant?.content) return false;
  return /\b(paste the (full )?job description|share the job description|once you share the job description|send the jd|paste the jd)\b/i.test(
    lastAssistant.content,
  );
}

export const ASK_OPTIMIZE_JD_REPLY =
  'I can tailor your resume for a specific role once you share the job description.\n\n' +
  '**Paste the full JD here** — include the role title and company if you have them. ' +
  'I\'ll open the optimization canvas and rewrite your resume when you send it.';

export const REMIND_OPTIMIZE_JD_REPLY =
  'Paste the full job description here (role title and company help too). ' +
  'I\'ll open the canvas and start optimizing as soon as you send it.';
